import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface EventReport {
  id: string;
  event_date: string;
  title: string;
  description: string;
  images: string[];
  created_at: string;
}

export default function NewsPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [eventReports, setEventReports] = useState<EventReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    checkAuth();
    fetchEventReports();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    } catch (error) {
      console.error('認証チェックエラー:', error);
      setIsAuthenticated(false);
    }
  };

  const handlePostClick = () => {
    if (isAuthenticated) {
      navigate('/news-post');
    } else {
      navigate('/login');
    }
  };

  const handleEdit = (reportId: string) => {
    navigate(`/news-edit/${reportId}`);
  };

  const handleDeleteClick = (reportId: string) => {
    setDeleteConfirm(reportId);
  };

  const handleDeleteConfirm = async (reportId: string) => {
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('event_reports')
        .delete()
        .eq('id', reportId);

      if (error) throw error;

      // リストから削除
      setEventReports(eventReports.filter(report => report.id !== reportId));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('削除エラー:', error);
      alert('削除に失敗しました。もう一度お試しください。');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  const fetchEventReports = async () => {
    try {
      const { data, error } = await supabase
        .from('event_reports')
        .select('*')
        .order('event_date', { ascending: false })
        .limit(10);

      if (error) throw error;
      setEventReports(data || []);
    } catch (error) {
      console.error('イベント報告の取得に失敗しました:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-sm'}`}>
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-4 md:py-6 px-4 md:px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold mb-1 tracking-wide" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                <Link to="/">兵庫中央岳風会</Link>
              </h1>
              <p className="text-xs md:text-sm opacity-90 font-light">公益社団法人日本詩吟学院認可</p>
            </div>
            <div className="text-left md:text-right text-xs md:text-sm font-light">
              <p className="flex items-center gap-2">
                <i className="ri-map-pin-line"></i>
                〒653-0867
              </p>
              <p className="mt-1">神戸市長田区高東町1-2-26</p>
            </div>
          </div>
        </div>
        <nav className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <ul className="flex flex-wrap justify-center items-center gap-1 md:gap-6 py-3 md:py-4">
              <li><Link to="/" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">トップページ</Link></li>
              <li><Link to="/about" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">会の紹介</Link></li>
              <li><Link to="/greeting" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">会長挨拶</Link></li>
              <li><Link to="/activities" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">活動計画</Link></li>
              <li><Link to="/membership" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">入会案内</Link></li>
              <li><Link to="/records" className="text-gray-800 hover:text-blue-800 font-medium text-xs md:text-sm transition-all hover:scale-105 px-2 md:px-3 py-1 rounded-md whitespace-nowrap">活動記録</Link></li>
            </ul>
          </div>
        </nav>
      </header>

      {/* Breadcrumb */}
      <div className="pt-32 md:pt-40 bg-gradient-to-b from-gray-50 to-white py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center space-x-2 text-xs md:text-sm text-gray-600">
            <Link to="/" className="hover:text-blue-800 whitespace-nowrap">トップ</Link>
            <span>›</span>
            <span>NEWS</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <header className="mb-8 md:mb-12 text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-blue-900 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              NEWS
            </h1>
            <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
              兵庫中央岳風会のイベント報告をご覧いただけます。
            </p>
          </header>

          <div className="bg-white rounded-2xl shadow-xl p-6 md:p-10 border-t-4 border-blue-800">
            <header className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-blue-900 flex items-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                <span className="w-3 h-3 md:w-4 md:h-4 bg-blue-800 rotate-45 mr-2 md:mr-3"></span>
                NEWS - イベント報告
              </h1>
              <p className="text-xs md:text-sm text-gray-600 mt-4">最新のイベント報告・お知らせを掲載しています。</p>
            </header>

            <div className="mb-8 text-right">
              <Link 
                to="/news-archive" 
                className="inline-flex items-center text-blue-800 hover:text-blue-900 font-medium text-xs md:text-sm transition-all hover:scale-105 bg-blue-50 px-4 py-2 rounded-lg whitespace-nowrap"
              >
                <i className="ri-archive-line mr-2"></i>
                過去のニュース（アーカイブ）
              </Link>
            </div>

            <article className="space-y-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              {loading ? (
                <div className="text-center py-12 md:py-16">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                    <i className="ri-loader-4-line text-3xl md:text-5xl text-blue-800 animate-spin"></i>
                  </div>
                  <p className="text-gray-600 text-sm md:text-base">読み込み中...</p>
                </div>
              ) : eventReports.length === 0 ? (
                <div className="text-center py-12 md:py-16">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-information-line text-3xl md:text-5xl text-blue-800"></i>
                  </div>
                  <p className="text-gray-600 text-sm md:text-base mb-2">現在、新しいお知らせはありません。</p>
                  <p className="text-xs md:text-sm text-gray-500">最新情報は随時更新いたします。</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {eventReports.map((report) => (
                    <div 
                      key={report.id} 
                      className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 md:p-8 border border-blue-100 hover:shadow-lg transition-all duration-300 relative"
                    >
                      {/* 管理者用ボタン */}
                      {isAuthenticated && (
                        <div className="absolute top-4 right-4 flex gap-2">
                          <button
                            onClick={() => handleEdit(report.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1 whitespace-nowrap"
                          >
                            <i className="ri-edit-line"></i>
                            編集
                          </button>
                          <button
                            onClick={() => handleDeleteClick(report.id)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-1 whitespace-nowrap"
                          >
                            <i className="ri-delete-bin-line"></i>
                            削除
                          </button>
                        </div>
                      )}

                      <div className="mb-4">
                        <h2 className="text-xl md:text-2xl font-bold text-blue-900 leading-relaxed mb-3" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                          {report.title}
                        </h2>
                        <div className="bg-blue-800 text-white px-4 py-2 rounded-lg inline-block">
                          <div className="text-xs font-medium">開催日</div>
                          <div className="text-sm font-bold">{formatDate(report.event_date)}</div>
                        </div>
                      </div>

                      {report.description && (
                        <p className="text-gray-700 text-sm md:text-base mb-6 leading-relaxed whitespace-pre-wrap">
                          {report.description}
                        </p>
                      )}

                      {report.images && report.images.length > 0 && (
                        <div className="mb-6">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                              <i className="ri-image-line"></i>
                              イベント写真（{report.images.length}枚）
                            </h3>
                            <Link
                              to="/gallery"
                              className="text-xs text-blue-800 hover:text-blue-900 font-medium flex items-center gap-1 hover:underline whitespace-nowrap"
                            >
                              ギャラリーで見る
                              <i className="ri-arrow-right-line"></i>
                            </Link>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {report.images.map((image, index) => (
                              <div 
                                key={index} 
                                className="aspect-square rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group"
                                onClick={() => window.open(image, '_blank')}
                              >
                                <img 
                                  src={image} 
                                  alt={`${report.title} - 写真${index + 1}`}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-gray-600 pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <i className="ri-calendar-line"></i>
                          <span>投稿日: {formatDate(report.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <i className="ri-price-tag-3-line"></i>
                          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">大会報告</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <div className="mt-8 text-center">
              <button
                onClick={handlePostClick}
                className="bg-blue-800 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-900 transition-all inline-flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-add-line text-xl"></i>
                新しいイベント報告を投稿
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-error-warning-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">削除の確認</h3>
              <p className="text-sm text-gray-600">
                このイベント報告を削除してもよろしいですか？<br />
                この操作は取り消せません。
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-3 rounded-lg font-bold transition-all disabled:opacity-50 whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDeleteConfirm(deleteConfirm)}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg font-bold transition-all disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                {deleting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    削除中...
                  </>
                ) : (
                  <>
                    <i className="ri-delete-bin-line"></i>
                    削除する
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-10 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>兵庫中央岳風会</h3>
            <p className="text-xs md:text-sm opacity-80">公益社団法人日本詩吟学院認可</p>
          </div>
          
          <nav className="mb-8 md:mb-10">
            <ul className="flex flex-wrap justify-center gap-3 md:gap-6">
              <li><Link to="/" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">トップページ</Link></li>
              <li><Link to="/about" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">会の紹介</Link></li>
              <li><Link to="/greeting" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">会長挨拶</Link></li>
              <li><Link to="/activities" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">活動計画</Link></li>
              <li><Link to="/membership" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">入会案内</Link></li>
              <li><Link to="/records" className="hover:text-gray-300 transition-colors text-xs md:text-sm whitespace-nowrap hover:underline">活動記録</Link></li>
            </ul>
          </nav>
          
          <div className="text-center text-xs md:text-sm opacity-90 border-t border-white/20 pt-6 md:pt-8">
            <p className="mb-2">© 2025 兵庫中央岳風会. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300 transition-colors whitespace-nowrap hover:underline">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
